"use client";

import {
	type KeyboardEvent as ReactKeyboardEvent,
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { cn } from "../../../lib";
import {
	resolveMediaPlayerContinuationLockStartPercent,
	resolveMediaPlayerScrubClampPercent,
	resolveSegmentPlayheadFillPercent,
} from "../../media-player/media-player-seek-math";
import { TOPIC_CARD_PLAYER_THEME } from "../../media-player/media-player-theme";
import { MediaPlayerTrackGlow } from "../../media-player/media-player-track-glow";
import { MediaPlayerTrackParticles } from "../../media-player/media-player-track-particles";
import {
	MEDIA_PLAYER_SEGMENT_DIVIDER_CLASSNAME,
	useMediaPlayerTrackSegmentTooltip,
} from "../../media-player/media-player-track-segment-tooltip";
import { MediaPlayerTrackSegmentTooltipLabel } from "../../media-player/media-player-track-segment-tooltip-label";
import { useMediaPlayerScrub } from "../../media-player/use-media-player-scrub";
import { useMediaPlayerSeekPin } from "../../media-player/use-media-player-seek-pin";
import { DESERT_AUDIO_WAVEFORM_AMBIENT } from "../audio-waveform-visualizer";
import { MediaPlayerSeekPinOverlay } from "../media-player-seek-pin-overlay";
import { INTERACTIVE_AUDIO_SCRUB_HIT_HEIGHT_PX } from "./constants";
import { InteractiveAudioSegmentWaveformColumn } from "./interactive-audio-segment-waveform-column";
import {
	mapInteractiveSegmentsToSeekSegments,
	resolveInteractiveAudioAriaValueText,
} from "./resolve-interactive-audio-aria-valuetext";
import { resolveSegmentWaveformLayout } from "./resolve-segment-waveform-layout";
import type {
	InteractiveAudioSegmentRegion,
	InteractiveAudioSegmentVisualState,
} from "./types";

export interface InteractiveAudioMultiSegmentWaveformTrackProps {
	progressPercent: number;
	maxSeekPercent?: number;
	furthestConsumedPercent?: number;
	continuationPercent?: number;
	scrubClampPercent?: number;
	segments: InteractiveAudioSegmentRegion[];
	segmentStates?: Record<string, InteractiveAudioSegmentVisualState>;
	isPreparingPlayback?: boolean;
	isLoading?: boolean;
	/** When false, the waveform track is not rendered (voice-sheet idle pattern). */
	isActive?: boolean;
	volumePercent?: number;
	showParticles?: boolean;
	/** Biome-aligned aurora colours; defaults to desert (voice sheet). */
	ambientParticleColor?: string;
	trackGlowColor1?: string;
	trackGlowColor2?: string;
	interactive?: boolean;
	scrubHitHeightPx?: number;
	ariaLabel?: string;
	onScrubPause?: () => void;
	onSeekCommit: (percent: number) => void;
	onSeekPreview?: (percent: number) => void;
	onSeekRejected?: () => void;
	className?: string;
	seekEpoch?: number;
	audioRef?: RefObject<HTMLAudioElement | null>;
}

const KEYBOARD_STEP_PERCENT = 1;
const KEYBOARD_SHIFT_STEP_PERCENT = 5;

export function InteractiveAudioMultiSegmentWaveformTrack({
	progressPercent,
	maxSeekPercent = 100,
	furthestConsumedPercent = maxSeekPercent,
	continuationPercent,
	scrubClampPercent: scrubClampPercentProp,
	segments,
	segmentStates = {},
	isPreparingPlayback = false,
	isLoading = false,
	isActive = true,
	volumePercent = 100,
	showParticles = true,
	ambientParticleColor = DESERT_AUDIO_WAVEFORM_AMBIENT.ambientParticleColor,
	trackGlowColor1 = DESERT_AUDIO_WAVEFORM_AMBIENT.trackGlowColor1,
	trackGlowColor2 = DESERT_AUDIO_WAVEFORM_AMBIENT.trackGlowColor2,
	interactive = true,
	scrubHitHeightPx = INTERACTIVE_AUDIO_SCRUB_HIT_HEIGHT_PX,
	ariaLabel = "Topic playback position",
	onScrubPause,
	onSeekCommit,
	onSeekPreview,
	onSeekRejected,
	className,
	seekEpoch = 0,
	audioRef,
}: InteractiveAudioMultiSegmentWaveformTrackProps) {
	const trackRef = useRef<HTMLDivElement>(null);
	const [reduceMotion, setReduceMotion] = useState(false);
	const [isTrackHovering, setIsTrackHovering] = useState(false);

	useEffect(() => {
		const media = window.matchMedia("(prefers-reduced-motion: reduce)");
		const update = () => setReduceMotion(media.matches);
		update();
		media.addEventListener("change", update);
		return () => media.removeEventListener("change", update);
	}, []);

	if (!isActive) {
		return null;
	}

	const showAmbientParticles = showParticles && !reduceMotion;

	const scrubClampPercent =
		scrubClampPercentProp ??
		resolveMediaPlayerScrubClampPercent(
			maxSeekPercent,
			continuationPercent,
			furthestConsumedPercent,
		);
	const continuationLockStartPercent =
		resolveMediaPlayerContinuationLockStartPercent(
			maxSeekPercent,
			furthestConsumedPercent,
			continuationPercent,
		);

	const layout = useMemo(
		() => resolveSegmentWaveformLayout(segments),
		[segments],
	);

	const seekSegments = useMemo(
		() => mapInteractiveSegmentsToSeekSegments(segments),
		[segments],
	);

	const handleScrubPause = useCallback(() => {
		onScrubPause?.();
	}, [onScrubPause]);

	const {
		playheadPercent,
		isScrubbing,
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerLeave,
		onPointerCancel,
	} = useMediaPlayerScrub({
		progressPercent,
		maxSeekPercent,
		scrubClampPercent,
		interactive,
		seekEpoch,
		trackBoundsRef: trackRef,
		onPause: handleScrubPause,
		onSeekCommit,
		onSeekPreview,
		onSeekRejected,
	});
	const { pinVisible, pinEnterMode, showLockIcon } = useMediaPlayerSeekPin({
		isScrubbing,
		isHovering: interactive && isTrackHovering,
		playheadPercent,
		scrubClampPercent,
		seekEpoch,
	});

	const showTrackGlow =
		showAmbientParticles &&
		(playheadPercent > 0 || (!isPreparingPlayback && !isLoading));

	const {
		trackTooltipLabel,
		trackTooltipAnchorCenterPercent,
		mergePointerHandlers,
	} = useMediaPlayerTrackSegmentTooltip(seekSegments, trackRef);

	const trackPointerHandlers = mergePointerHandlers({
		onPointerDown,
		onPointerMove,
		onPointerUp,
		onPointerLeave,
		onPointerCancel,
	});

	const ariaValueText = resolveInteractiveAudioAriaValueText(
		playheadPercent,
		segments,
	);

	const commitKeyboardPercent = useCallback(
		(nextPercent: number) => {
			const clamped = Math.max(
				0,
				Math.min(scrubClampPercent, nextPercent),
			);
			if (clamped > scrubClampPercent + 0.001) {
				onSeekRejected?.();
				return;
			}
			handleScrubPause();
			onSeekCommit(clamped);
		},
		[handleScrubPause, onSeekCommit, onSeekRejected, scrubClampPercent],
	);

	const handleKeyDown = useCallback(
		(event: ReactKeyboardEvent<HTMLDivElement>) => {
			if (!interactive) {
				return;
			}

			const step = event.shiftKey
				? KEYBOARD_SHIFT_STEP_PERCENT
				: KEYBOARD_STEP_PERCENT;

			switch (event.key) {
				case "ArrowLeft":
					event.preventDefault();
					commitKeyboardPercent(playheadPercent - step);
					break;
				case "ArrowRight":
					event.preventDefault();
					commitKeyboardPercent(playheadPercent + step);
					break;
				case "Home":
					event.preventDefault();
					commitKeyboardPercent(0);
					break;
				case "End":
					event.preventDefault();
					commitKeyboardPercent(scrubClampPercent);
					break;
				default:
					break;
			}
		},
		[
			commitKeyboardPercent,
			interactive,
			playheadPercent,
			scrubClampPercent,
		],
	);

	const particleTheme = useMemo(
		() => ({
			...TOPIC_CARD_PLAYER_THEME,
			ambientParticleColor,
			trackGlowColor1,
			trackGlowColor2,
		}),
		[ambientParticleColor, trackGlowColor1, trackGlowColor2],
	);

	return (
		<div
			className={cn(
				"relative isolate h-8 min-w-0 flex-1 overflow-visible",
				className,
			)}
			onPointerEnter={
				interactive
					? () => {
							setIsTrackHovering(true);
						}
					: undefined
			}
			onPointerLeave={
				interactive
					? () => {
							setIsTrackHovering(false);
						}
					: undefined
			}
		>
			{showAmbientParticles ? (
				<>
					<MediaPlayerTrackParticles theme={particleTheme} />
					{showTrackGlow ? (
						<MediaPlayerTrackGlow
							playheadPercent={playheadPercent}
							className="-top-3 -bottom-3"
							color1={trackGlowColor1}
							color2={trackGlowColor2}
							brightness={1.05}
							bandSpread={1.3}
							bandHeight={0.5}
						/>
					) : null}
				</>
			) : null}
			<div
				ref={trackRef}
				className="relative z-[1] flex h-full w-full touch-none select-none items-stretch overflow-visible"
				role="slider"
				tabIndex={interactive ? 0 : -1}
				aria-valuemin={0}
				aria-valuemax={100}
				aria-valuenow={Math.round(playheadPercent)}
				aria-valuetext={ariaValueText}
				aria-label={ariaLabel}
				aria-disabled={!interactive}
				onKeyDown={handleKeyDown}
			>
				{layout.map((slot, slotIndex) => {
					const state = segmentStates[slot.segmentId];
					const fillPercent = resolveSegmentPlayheadFillPercent(
						{
							startPercent: slot.startPercent,
							endPercent: slot.endPercent,
						},
						playheadPercent,
					);
					const isLocked =
						slot.startPercent >= continuationLockStartPercent;
					const isLastSlot = slotIndex === layout.length - 1;
					const playheadInSlot =
						playheadPercent >= slot.startPercent &&
						(isLastSlot
							? playheadPercent <= slot.endPercent
							: playheadPercent < slot.endPercent);
					const isLiveColumn =
						playheadInSlot && state?.waveformMode !== "preparing";

					return (
						<div
							key={slot.segmentId}
							className="relative flex min-w-0 items-center"
							style={{ width: `${slot.widthPercent}%` }}
						>
							<InteractiveAudioSegmentWaveformColumn
								barCount={slot.barCount}
								waveformSeed={
									state?.waveformSeed ?? slot.segmentId
								}
								fillPercent={fillPercent}
								volumePercent={volumePercent}
								displayRevealPercent={
									state?.displayRevealPercent ?? 0
								}
								showBufferReveal={
									segmentStates[slot.segmentId]
										?.waveformMode === "preparing"
								}
								bufferRevealSessionKey={
									state?.bufferRevealSessionKey
								}
								bufferRevealSessionStartMs={
									state?.bufferRevealSessionStartMs
								}
								isLocked={isLocked}
								audioRef={audioRef}
								waveformTimeline={state?.waveformTimeline}
								isLive={isLiveColumn}
								isPreparingPlayback={isPreparingPlayback}
							/>
						</div>
					);
				})}
				{layout.slice(1).map((slot) => (
					<div
						key={`divider-${slot.segmentId}`}
						className={cn(
							"pointer-events-none absolute top-0 bottom-0 z-[2] bg-black/25 dark:bg-black/25",
							MEDIA_PLAYER_SEGMENT_DIVIDER_CLASSNAME,
						)}
						style={{ left: `${slot.startPercent}%` }}
						aria-hidden
					/>
				))}
				{continuationLockStartPercent < 100 ? (
					<div
						className="pointer-events-none absolute inset-y-0 right-0 z-[3] bg-background/20"
						style={{ left: `${continuationLockStartPercent}%` }}
						aria-hidden
					/>
				) : null}
				<MediaPlayerSeekPinOverlay
					pinVisible={pinVisible}
					pinEnterMode={pinEnterMode}
					showLockIcon={showLockIcon}
					playheadPercent={playheadPercent}
					interactive={interactive}
					onPointerDown={trackPointerHandlers.onPointerDown}
					onPointerMove={trackPointerHandlers.onPointerMove}
					onPointerUp={trackPointerHandlers.onPointerUp}
					onPointerLeave={trackPointerHandlers.onPointerLeave}
					onPointerCancel={trackPointerHandlers.onPointerCancel}
				/>
			</div>
			{interactive ? (
				<div
					className="pointer-events-none absolute inset-x-0 top-1/2 z-[5] -translate-y-1/2"
					style={{ height: scrubHitHeightPx }}
				>
					{scrubClampPercent > 0 ? (
						<div
							className="pointer-events-auto absolute inset-y-0 left-0 cursor-pointer touch-none select-none"
							style={{
								width: `${scrubClampPercent}%`,
								minWidth: "12px",
							}}
							onPointerDown={trackPointerHandlers.onPointerDown}
							onPointerMove={trackPointerHandlers.onPointerMove}
							onPointerUp={trackPointerHandlers.onPointerUp}
							onPointerLeave={trackPointerHandlers.onPointerLeave}
							onPointerCancel={
								trackPointerHandlers.onPointerCancel
							}
						/>
					) : null}
					{continuationLockStartPercent < 100 ? (
						<div
							className="pointer-events-auto absolute inset-y-0 right-0 cursor-not-allowed touch-none select-none"
							style={{ left: `${continuationLockStartPercent}%` }}
							onPointerDown={(
								event: ReactPointerEvent<HTMLDivElement>,
							) => {
								event.preventDefault();
								onSeekRejected?.();
							}}
						/>
					) : null}
				</div>
			) : null}
			{trackTooltipLabel != null &&
			trackTooltipAnchorCenterPercent != null ? (
				<MediaPlayerTrackSegmentTooltipLabel
					label={trackTooltipLabel}
					anchorCenterPercent={trackTooltipAnchorCenterPercent}
				/>
			) : null}
		</div>
	);
}
